import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ReactComponent as Urgent } from "../../../assets/images/TicketImages/urgent.svg";
import { ReactComponent as High } from "../../../assets/images/TicketImages/high.svg";
import { ReactComponent as Medium } from "../../../assets/images/TicketImages/medium.svg";
import { ReactComponent as Low } from "../../../assets/images/TicketImages/low.svg";
import { ReactComponent as SearchIcon } from "../../../assets/images/TicketImages/search.svg";
import { ReactComponent as ChatTag } from "../../../assets/images/ChatImages/chattag.svg";
import { colorCodesForAvatar } from "../../../constants/index";
import { chracterPosition } from "../../../utils/index";
import { ListCoRoleName } from "../../../store/features/ListCoUserByRole";
import { updateTicket } from "../../../store/features/updateTicket";
import { fetchTags } from "../../../store/features/tagsApi";
import {
  updateTicketOptimistic,
  adjustStatusCountsForStatusChange,
} from "../../../store/features/getAllTickets";
import { normalizeTagIdsFromRaw } from "../../../utils/ticketV2DetailsUtils";
import {
  IconBulkAssignee16,
  IconBulkFlag16,
  IconBulkStatus16,
  IconBulkTag16,
  IconClose16,
} from "./TicketV2Icons";

const inter = '"Inter", sans-serif';
const MAX_TICKET_TAGS = 5;

const STATUS_OPTIONS = [
  { value: "open", label: "Submit as Open", color: "#148DFD" },
  { value: "solved", label: "Submit as Solved", color: "#52C41A" },
  { value: "pending", label: "Submit as Pending", color: "#FA8C16" },
  { value: "spam", label: "Submit as Spam", color: "#93A1B0" },
];

const PRIORITY_OPTIONS = [
  { value: "urgent", label: "Urgent", Icon: Urgent },
  { value: "high", label: "High", Icon: High },
  { value: "medium", label: "Medium", Icon: Medium },
  { value: "low", label: "Low", Icon: Low, iconSx: { marginLeft: "5px" } },
];

const ACTIONS = [
  { id: "status", label: "Status", Icon: IconBulkStatus16 },
  { id: "priority", label: "Priority", Icon: IconBulkFlag16 },
  { id: "assignee", label: "Assignee", Icon: IconBulkAssignee16 },
  { id: "tags", label: "Tags", Icon: IconBulkTag16 },
];

function StatusDot({ color }) {
  return (
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: "50px",
        border: `1px dashed ${color}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: color,
          flexShrink: 0,
        }}
      />
    </Box>
  );
}

function isMongoObjectId(id) {
  return typeof id === "string" && /^[a-fA-F0-9]{24}$/.test(id);
}

const menuPaperSx = {
  mt: 0.5,
  width: 264,
  maxWidth: "calc(100vw - 32px)",
  borderRadius: "8px",
  border: "1px solid #D5DCE1",
  boxShadow: "0px 15px 20px 0px rgba(6, 21, 34, 0.05)",
  overflow: "hidden",
};

const menuItemSx = {
  width: "100%",
  justifyContent: "flex-start",
  gap: 1,
  px: 1,
  py: 0,
  minHeight: 40,
  borderRadius: "8px",
  fontFamily: inter,
  textAlign: "left",
  "&:hover": { bgcolor: "#F9FAFA" },
};

function TicketBulkActions({
  selectedIds = [],
  tickets = [],
  onExit,
  onSelectAll,
  onApplied,
}) {
  const dispatch = useDispatch();
  const [activeMenu, setActiveMenu] = useState(null);
  const [applying, setApplying] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const actionBarRef = useRef(null);

  const businessId = useSelector(
    (state) => state.loginToken?.response?.user?.business_id
  );
  const currentUser = useSelector((state) => state.loginToken?.response?.user);
  const availableTags = useSelector((state) => state.tags?.list || []);

  const selectedCount = selectedIds.length;
  const allSelected =
    tickets.length > 0 && selectedIds.length >= tickets.length;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const response = await dispatch(
        ListCoRoleName({ name: "agent,admin,admin_with_billing" })
      );
      if (!cancelled && response.payload?.status === 200) {
        setTeamMembers(response.payload.data || []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (businessId) {
      dispatch(fetchTags({ businessId, type: "conversation" }));
    }
  }, [dispatch, businessId]);

  const filteredMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return teamMembers;
    return teamMembers.filter(
      (m) =>
        (m.full_name || "").toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q)
    );
  }, [teamMembers, memberSearch]);

  const filteredTags = useMemo(() => {
    const q = tagSearch.trim().toLowerCase();
    const list = (availableTags || []).map((tag) => ({
      _id: tag._id || tag.name || String(tag),
      name: tag.name || String(tag),
    }));
    if (!q) return list;
    return list.filter((t) => t.name.toLowerCase().includes(q));
  }, [availableTags, tagSearch]);

  const closeMenu = useCallback(() => {
    setActiveMenu(null);
    setMemberSearch("");
    setTagSearch("");
  }, []);

  const openMenu = (actionId) => {
    if (activeMenu === actionId) {
      closeMenu();
      return;
    }
    setActiveMenu(actionId);
  };

  const selectedTickets = useMemo(
    () => tickets.filter((t) => selectedIds.includes(t._id)),
    [tickets, selectedIds]
  );

  const runBulkUpdate = useCallback(
    async (updateData, { successMessage } = {}) => {
      if (!selectedIds.length || applying) return;
      setApplying(true);
      try {
        await Promise.all(
          selectedIds.map(async (ticketId) => {
            const ticket = tickets.find((t) => t._id === ticketId);
            if (!ticket) return;
            if (updateData.status && updateData.status !== ticket.status) {
              dispatch(
                adjustStatusCountsForStatusChange({
                  oldStatus: ticket.status,
                  newStatus: updateData.status,
                })
              );
            }
            const next = { ...ticket, ...updateData };
            if (updateData.assignee && typeof updateData.assignee === "object") {
              next.assignee = updateData.assignee;
            }
            dispatch(updateTicketOptimistic(next));
            await dispatch(
              updateTicket({ ticketId, updateData })
            ).unwrap();
          })
        );
        toast.success(successMessage || "Updates applied successfully");
        closeMenu();
        onApplied?.();
      } catch (error) {
        console.error("Bulk update failed:", error);
        toast.error(
          error?.data?.message || error?.message || "Failed to apply updates"
        );
        onApplied?.();
      } finally {
        setApplying(false);
      }
    },
    [selectedIds, applying, tickets, dispatch, closeMenu, onApplied]
  );

  const handleStatusSelect = (status) => {
    runBulkUpdate(
      { status },
      { successMessage: `Status updated for ${selectedCount} ticket(s)` }
    );
  };

  const handlePrioritySelect = (priority) => {
    runBulkUpdate(
      { priority },
      { successMessage: `Priority updated for ${selectedCount} ticket(s)` }
    );
  };

  const handleAssigneeSelect = (member) => {
    if (!member?._id) return;
    runBulkUpdate(
      {
        assignee: {
          _id: member._id,
          full_name: member.full_name,
          agent_image: member.agent_image || null,
        },
      },
      { successMessage: `Assignee updated for ${selectedCount} ticket(s)` }
    );
  };

  const handleAssignToMe = () => {
    const me =
      teamMembers.find((m) => m._id === currentUser?._id) ||
      (currentUser
        ? {
            _id: currentUser._id,
            full_name: currentUser.full_name,
            agent_image: currentUser.agent_image || null,
          }
        : null);
    if (me) handleAssigneeSelect(me);
  };

  const handleTagSelect = async (tag) => {
    if (!tag?._id || !isMongoObjectId(String(tag._id)) || applying) return;
    const tagId = String(tag._id);
    setApplying(true);
    try {
      let anySkipped = false;
      let anyUpdated = false;
      await Promise.all(
        selectedTickets.map(async (ticket) => {
          const existingIds = normalizeTagIdsFromRaw(
            ticket.tags,
            availableTags
          );
          if (existingIds.includes(tagId)) return;
          if (existingIds.length >= MAX_TICKET_TAGS) {
            anySkipped = true;
            return;
          }
          const snapshot = [...existingIds, tagId];
          const nextTags = [
            ...(Array.isArray(ticket.tags) ? ticket.tags : []),
            { _id: tagId, name: tag.name },
          ];
          dispatch(updateTicketOptimistic({ ...ticket, tags: nextTags }));
          // Same as Ticket V2 details: PATCH ticket with full tags id list.
          await dispatch(
            updateTicket({
              ticketId: ticket._id,
              updateData: { tags: snapshot },
            })
          ).unwrap();
          anyUpdated = true;
        })
      );
      if (anySkipped) {
        toast.info(
          `Some tags were not added: each ticket can have at most ${MAX_TICKET_TAGS} tags.`
        );
      } else if (anyUpdated) {
        toast.success(`Tag added to ${selectedCount} ticket(s)`);
      } else {
        toast.info("Tag already on selected ticket(s)");
      }
      closeMenu();
      onApplied?.();
    } catch (error) {
      console.error("Bulk tag failed:", error);
      toast.error(
        error?.data?.message || error?.message || "Failed to add tags"
      );
      onApplied?.();
    } finally {
      setApplying(false);
    }
  };

  const menuOpen = Boolean(activeMenu) && Boolean(actionBarRef.current);

  return (
    <Box sx={{ flexShrink: 0, borderBottom: "1px solid #E5E9EC" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, pt: 1.5, pb: 1 }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <IconButton
            size="small"
            onClick={onExit}
            aria-label="Exit bulk selection"
            sx={{ p: 0, color: "#405261", width: 16, height: 16 }}
          >
            <IconClose16 />
          </IconButton>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: "#061522",
              fontFamily: inter,
              lineHeight: "18px",
            }}
          >
            {selectedCount}{" "}
            <Box component="span" sx={{ fontWeight: 400, color: "#405261" }}>
              / Selected
            </Box>
          </Typography>
        </Stack>
        <ButtonBase
          onClick={onSelectAll}
          disabled={!tickets.length}
          sx={{
            color: "#148DFD",
            fontSize: 12,
            fontWeight: 400,
            fontFamily: inter,
            lineHeight: "18px",
            textDecoration: "underline",
            textUnderlineOffset: "2px",
          }}
        >
          {allSelected ? "Deselect all" : "Select all"}
        </ButtonBase>
      </Stack>

      <Box
        ref={actionBarRef}
        sx={{
          mx: 1,
          mb: 1.5,
          bgcolor: "#F7F8F9",
          borderRadius: "8px",
          height: 48,
          display: "flex",
          alignItems: "stretch",
          position: "relative",
        }}
      >
        {ACTIONS.map((action) => {
          const isActive = activeMenu === action.id;
          const Icon = action.Icon;
          return (
            <ButtonBase
              key={action.id}
              disabled={applying || selectedCount === 0}
              onClick={() => openMenu(action.id)}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                borderRadius: "8px",
                bgcolor: isActive ? "#fff" : "transparent",
                boxShadow: isActive
                  ? "0px 1px 2px 0px rgba(6, 21, 34, 0.05)"
                  : "none",
                m: "2px",
                minWidth: 0,
              }}
            >
              <Icon stroke={isActive ? "#061522" : "#405261"} />
              <Typography
                sx={{
                  fontSize: 8,
                  fontWeight: 400,
                  lineHeight: "18px",
                  color: isActive ? "#061522" : "#405261",
                  fontFamily: inter,
                }}
              >
                {action.label}
              </Typography>
            </ButtonBase>
          );
        })}
        {applying && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(247,248,249,0.72)",
              borderRadius: "8px",
            }}
          >
            <CircularProgress size={18} sx={{ color: "#148DFD" }} />
          </Box>
        )}
      </Box>

      <Popover
        open={menuOpen}
        anchorEl={actionBarRef.current}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: { sx: menuPaperSx },
        }}
      >
        {activeMenu === "status" && (
          <Stack sx={{ p: 1, gap: 0.5 }}>
            {STATUS_OPTIONS.map((opt) => (
              <ButtonBase
                key={opt.value}
                onClick={() => handleStatusSelect(opt.value)}
                sx={menuItemSx}
              >
                <StatusDot color={opt.color} />
                <Typography
                  sx={{ fontSize: 14, color: "#405261", fontFamily: inter }}
                >
                  {opt.label}
                </Typography>
              </ButtonBase>
            ))}
          </Stack>
        )}

        {activeMenu === "priority" && (
          <Stack sx={{ p: 1, gap: 0.5 }}>
            {PRIORITY_OPTIONS.map((opt) => {
              const ItemIcon = opt.Icon;
              return (
                <ButtonBase
                  key={opt.value}
                  onClick={() => handlePrioritySelect(opt.value)}
                  sx={menuItemSx}
                >
                  <Box
                    sx={{
                      width: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <ItemIcon style={opt.iconSx} />
                  </Box>
                  <Typography
                    sx={{ fontSize: 14, color: "#405261", fontFamily: inter }}
                  >
                    {opt.label}
                  </Typography>
                </ButtonBase>
              );
            })}
          </Stack>
        )}

        {activeMenu === "assignee" && (
          <Box sx={{ p: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search team member"
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ width: 16, height: 16 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1,
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: "8px",
                  fontSize: 14,
                  fontFamily: inter,
                  bgcolor: "#fff",
                  "& fieldset": { borderColor: "#D5DCE1" },
                },
              }}
            />
            <Box sx={{ maxHeight: 168, overflowY: "auto" }}>
              <ButtonBase
                onClick={handleAssignToMe}
                sx={{ ...menuItemSx, mb: 0.5 }}
              >
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: "#F1F3F5",
                    color: "#405261",
                  }}
                >
                  <IconBulkAssignee16 stroke="#405261" />
                </Avatar>
                <Typography
                  sx={{ fontSize: 14, color: "#405261", fontFamily: inter }}
                >
                  Assign to me
                </Typography>
              </ButtonBase>
              {filteredMembers.map((member) => {
                const name = member.full_name || "—";
                const initial = name.substring(0, 1).toUpperCase();
                const bg =
                  colorCodesForAvatar[chracterPosition(initial)] || "#757575";
                return (
                  <ButtonBase
                    key={member._id}
                    onClick={() => handleAssigneeSelect(member)}
                    sx={{ ...menuItemSx, mb: 0.5 }}
                  >
                    {member.agent_image ? (
                      <Avatar
                        src={member.agent_image}
                        alt={name}
                        sx={{ width: 24, height: 24 }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: bg,
                          fontSize: 11,
                        }}
                      >
                        {initial}
                      </Avatar>
                    )}
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "#061522",
                        fontFamily: inter,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {name}
                    </Typography>
                  </ButtonBase>
                );
              })}
              {filteredMembers.length === 0 && (
                <Typography
                  sx={{
                    px: 1,
                    py: 2,
                    fontSize: 13,
                    color: "#8B97A2",
                    fontFamily: inter,
                    textAlign: "center",
                  }}
                >
                  No team members found
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {activeMenu === "tags" && (
          <Box sx={{ p: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search tags"
              value={tagSearch}
              onChange={(e) => setTagSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ width: 16, height: 16 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1,
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: "8px",
                  fontSize: 14,
                  fontFamily: inter,
                  bgcolor: "#fff",
                  "& fieldset": { borderColor: "#D5DCE1" },
                },
              }}
            />
            <Box sx={{ maxHeight: 168, overflowY: "auto" }}>
              {filteredTags.map((tag) => (
                <ButtonBase
                  key={tag._id}
                  onClick={() => handleTagSelect(tag)}
                  sx={{ ...menuItemSx, mb: 0.5 }}
                >
                  <ChatTag style={{ width: 16, height: 16, flexShrink: 0 }} />
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "#405261",
                      fontFamily: inter,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tag.name}
                  </Typography>
                </ButtonBase>
              ))}
              {filteredTags.length === 0 && (
                <Typography
                  sx={{
                    px: 1,
                    py: 2,
                    fontSize: 13,
                    color: "#8B97A2",
                    fontFamily: inter,
                    textAlign: "center",
                  }}
                >
                  No tags found
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
}

export default TicketBulkActions;
